import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';

type DateRange = { from?: string; to?: string };

/**
 * HorizonTrackingService
 *
 * Staff-side Horizon endpoints (docs/backend/HORIZON_REMMI_FRONTEND(2).md). Horizon activity stays
 * attached to the existing Remmi lead — there is no separate Horizon customer record to look up.
 * Blank `from` / `to` are left off the request so the backend applies its own default window.
 */
@Injectable({
  providedIn: 'root',
})
export class HorizonTrackingService {
  constructor(private api: ApiService) {}

  // ---- Lead: access (§3) ----------------------------------------------------

  getLeadAccess = (leadId: string): Observable<any> => {
    return this.api.get(`/leads/${leadId}/horizon-access`);
  };

  inviteLead = (leadId: string, email?: string): Observable<any> => {
    return this.api.post(`/leads/${leadId}/horizon-invite`, email ? { email } : {});
  };

  /** `suspend`, `restore` or `cancel-invite`; each returns the same payload as `getLeadAccess`. */
  changeLeadAccess = (leadId: string, action: 'suspend' | 'restore' | 'cancel-invite'): Observable<any> => {
    return this.api.post(`/leads/${leadId}/horizon-access/${action}`, {});
  };

  /** Emails an active buyer a single-use "set password and sign in" link (§3.3); `422 not_active` otherwise. */
  sendLeadAccessLink = (leadId: string): Observable<any> => {
    return this.api.post(`/leads/${leadId}/horizon-access/send-reset-link`, {});
  };

  /** Makes (or stops making) this lead's project the customer's main Horizon project. A customer has at most one. */
  setLeadPrimaryProject = (leadId: string, primary: boolean): Observable<any> => {
    return this.api.post(`/leads/${leadId}/horizon-primary`, { primary });
  };

  /** Sets the buyer's Horizon stage on this project (enquiry, in progress, construction or purchased). */
  setHorizonStage = (leadId: string, projectId: string, stage: string): Observable<any> => {
    return this.api.put(`/leads/${leadId}/horizon/stage`, { project_id: projectId, stage });
  };

  // ---- Lead: tracking (§4) --------------------------------------------------

  // The lead tab passes the lead's own project so a buyer who browsed several projects only shows this one's activity.

  getLeadOverview = (leadId: string, range: DateRange, projectId?: string | null): Observable<any> => {
    return this.api.get(`/leads/${leadId}/horizon/overview`, { ...this.rangeParams(range), ...this.leadProjectParam(projectId) });
  };

  getLeadActivity = (leadId: string, range: DateRange, type: string, page: number, limit = 20, projectId?: string | null): Observable<any> => {
    return this.api.get(`/leads/${leadId}/horizon/activity`, {
      ...this.rangeParams(range),
      ...this.leadProjectParam(projectId),
      type,
      page: String(page),
      limit: String(limit),
    });
  };

  getLeadDocuments = (leadId: string, range: DateRange): Observable<any> => {
    return this.api.get(`/leads/${leadId}/horizon/documents`, this.rangeParams(range));
  };

  getLeadProperties = (leadId: string, range: DateRange, limit?: number, page?: number, projectId?: string | null): Observable<any> => {
    return this.api.get(`/leads/${leadId}/horizon/properties`, {
      ...this.rangeParams(range),
      ...this.leadProjectParam(projectId),
      ...(limit ? { limit: String(limit) } : {}),
      ...(page ? { page: String(page) } : {}),
    });
  };

  /**
   * Most Viewed Properties on the lead tab: the same per-buyer endpoint as Recent Property Activity,
   * asked for `sort=views` so the buyer's most viewed lots come first.
   */
  getLeadMostViewed = (leadId: string, range: DateRange, limit: number, projectId?: string | null): Observable<any> => {
    return this.api.get(`/leads/${leadId}/horizon/properties`, {
      ...this.rangeParams(range),
      ...this.leadProjectParam(projectId),
      sort: 'views',
      order: 'desc',
      limit: String(limit),
      page: '1',
    });
  };

  getLeadInsights = (leadId: string, range: DateRange, projectId?: string | null): Observable<any> => {
    return this.api.get(`/leads/${leadId}/horizon/insights`, { ...this.rangeParams(range), ...this.leadProjectParam(projectId) });
  };

  // ---- Lead: shared with buyer, preferences, shortlist (redesign §5, §6) --------------------

  getLeadShares = (leadId: string, page: number, limit = 10): Observable<any> => {
    return this.api.get(`/leads/${leadId}/horizon/shares`, { page: String(page), limit: String(limit) });
  };

  createLeadShare = (leadId: string, payload: any): Observable<any> => {
    return this.api.post(`/leads/${leadId}/horizon/shares`, payload);
  };

  deleteLeadShare = (leadId: string, shareId: string): Observable<any> => {
    return this.api.delete(`/leads/${leadId}/horizon/shares/${shareId}`);
  };

  getLeadPreferences = (leadId: string): Observable<any> => {
    return this.api.get(`/leads/${leadId}/horizon/preferences`);
  };

  saveLeadPreferences = (leadId: string, payload: any): Observable<any> => {
    return this.api.put(`/leads/${leadId}/horizon/preferences`, payload);
  };

  getLeadShortlist = (leadId: string, projectId?: string | null): Observable<any> => {
    return this.api.get(`/leads/${leadId}/horizon/shortlist`, this.leadProjectParam(projectId));
  };

  // ---- Project panels used on the lead tab (redesign §3, §7, §8) -----------------------------

  getMatrixHeatmap = (
    projectId: string,
    range: DateRange,
    query: { view_by: string; building_id?: string; level?: string; floorplan?: string; scope?: 'viewer'; lead_id?: string }
  ): Observable<any> => {
    const params: Record<string, string> = { ...this.projectParams(range), view_by: query.view_by };
    if (query.building_id) {
      params['building_id'] = query.building_id;
    }
    if (query.level) {
      params['level'] = query.level;
    }
    if (query.floorplan) {
      params['floorplan'] = query.floorplan;
    }
    if (query.scope) {
      params['scope'] = query.scope;
    }
    // The lead tab colours the grid by this one buyer's activity only.
    if (query.lead_id) {
      params['lead_id'] = query.lead_id;
    }
    return this.api.get(`/projects/${projectId}/horizon/matrix-heatmap`, params);
  };

  getProjectMostViewed = (projectId: string, range: DateRange, limit = 5, scope?: 'viewer'): Observable<any> => {
    return this.api.get(`/projects/${projectId}/horizon/properties`, {
      ...this.projectParams(range),
      metric: 'viewed',
      limit: String(limit),
      ...(scope ? { scope } : {}),
    });
  };

  // ---- Project report: activity feed and document detail (reporting redesign §9, §11) ---------

  getProjectActivity = (projectId: string, range: DateRange, type: string, page: number, limit = 10): Observable<any> => {
    return this.api.get(`/projects/${projectId}/horizon/activity`, {
      ...this.projectParams(range),
      type,
      page: String(page),
      limit: String(limit),
      scope: 'viewer',
    });
  };

  getProjectDocumentEngagement = (projectId: string, documentId: string, range: DateRange): Observable<any> => {
    return this.api.get(`/projects/${projectId}/horizon/documents/${documentId}/engagement`, this.projectParams(range));
  };

  getProjectSharedDocuments = (projectId: string): Observable<any> => {
    return this.api.get(`/projects/${projectId}/horizon/documents/shared`);
  };

  /** Lots and approved documents the agent may share with a buyer. */
  getShareable = (projectId: string): Observable<any> => {
    return this.api.get(`/projects/${projectId}/horizon/shareable`);
  };

  getPreferenceOptions = (projectId: string): Observable<any> => {
    return this.api.get(`/projects/${projectId}/horizon/preference-options`);
  };

  getLeadHeatMap = (leadId: string, range: DateRange): Observable<any> => {
    return this.api.get(`/leads/${leadId}/horizon/heatmap`, this.rangeParams(range));
  };

  // ---- Lot (§10.4) --------------------------------------------------------------

  /** With `leadId` the summary and buyer list are that one buyer (a non-manager may only ask about their own lead). */
  getUnitEngagement = (unitId: string, range: DateRange, leadId?: string | null): Observable<any> => {
    return this.api.get(`/units/${unitId}/horizon-engagement`, { ...this.rangeParams(range), ...(leadId ? { lead_id: leadId } : {}) });
  };

  // ---- Project reporting (§5) -----------------------------------------------

  getProjectSummary = (projectId: string, range: DateRange): Observable<any> => {
    return this.api.get(`/projects/${projectId}/horizon/summary`, this.projectParams(range));
  };

  /** Lead Engagement table. "Most engaged" is `sort=engagement`. */
  getProjectLeads = (projectId: string, range: DateRange, query: { sort: string; order: string; page: number; limit: number }): Observable<any> => {
    return this.api.get(`/projects/${projectId}/horizon/leads`, {
      ...this.projectParams(range),
      sort: query.sort,
      order: query.order,
      page: String(query.page),
      limit: String(query.limit),
    });
  };

  getProjectEngagementOverTime = (projectId: string, range: DateRange): Observable<any> => {
    return this.api.get(`/projects/${projectId}/horizon/engagement-over-time`, { ...this.projectParams(range), interval: 'day' });
  };

  getProjectVisitorType = (projectId: string, range: DateRange): Observable<any> => {
    return this.api.get(`/projects/${projectId}/horizon/visitor-type`, this.projectParams(range));
  };

  getProjectDocuments = (projectId: string, range: DateRange): Observable<any> => {
    return this.api.get(`/projects/${projectId}/horizon/documents`, this.projectParams(range));
  };

  getProjectInsights = (projectId: string, range: DateRange): Observable<any> => {
    return this.api.get(`/projects/${projectId}/horizon/insights`, this.projectParams(range));
  };

  getProjectProperties = (
    projectId: string,
    range: DateRange,
    metric: 'viewed' | 'shortlisted' | 'trending',
    limit?: number,
    scope?: 'viewer'
  ): Observable<any> => {
    return this.api.get(`/projects/${projectId}/horizon/properties`, {
      ...this.projectParams(range),
      metric,
      ...(limit ? { limit: String(limit) } : {}),
      ...(scope ? { scope } : {}),
    });
  };

  /** `drillParams` is a node's `drill_params`, sent untouched; at the top level pass just `{ level }`. */
  getProjectHeatMap = (projectId: string, range: DateRange, drillParams: Record<string, string>): Observable<any> => {
    return this.api.get(`/projects/${projectId}/horizon/heatmap`, { ...this.projectParams(range), ...drillParams });
  };

  private rangeParams(range: DateRange): Record<string, string> {
    return {
      ...(range?.from ? { from: range.from } : {}),
      ...(range?.to ? { to: range.to } : {}),
    };
  }

  private leadProjectParam(projectId?: string | null): Record<string, string> {
    return projectId ? { project_id: projectId } : {};
  }

  /** Reporting endpoints also take the usual `officeid`. */
  private projectParams(range: DateRange): Record<string, string> {
    return { officeid: this.api.getOfficeId(), ...this.rangeParams(range) };
  }
}
